import React, { useEffect, useState } from 'react'
import { Col, Row, Form as RBForm } from 'react-bootstrap'
import { Form, Formik } from 'formik'
import { ShimmerButton, ShimmerText, ShimmerTitle } from 'react-shimmer-effects'
import { Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { number, object, string } from 'yup'
import { Button, useUserService, useErrorService } from '@abroad/components'
import { renderPlanPayment } from './CoachingPlanInformation'
import { InputField } from '../form'
import API from '../../utils/API'

const defaultAddress = {
  line1: '',
  line2: '',
  state: '',
  city: '',
  country: '',
  postal_code: '',
  billerName: '',
}

const validationSchema = object().shape({
  line1: string().notRequired(),
  line2: string().notRequired(),
  state: string().notRequired(),
  country: string().notRequired(),
  city: string().notRequired(),
  postal_code: number().integer().notRequired(),
  billerName: string().required('Biller Name is required'),
})

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY)

const CoachingPlanPaymentForm = ({
  selectedCardDetails,
  staticSOWData,
  subscription,
  onSuccess,
  isLoading: isPaying,
  onBack = () => {},
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [billingAddress, setBillingAddress] = useState(defaultAddress)
  const [intialStateAddressObj, setIntialStateAddressObj] =
    useState(defaultAddress)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const elements = useElements()
  const stripe = useStripe()
  const { user } = useUserService()
  const Error = useErrorService()
  const userId = user?._id

  useEffect(() => {
    const getUserAddress = async () => {
      setIsLoading(true)
      try {
        const { data } = await API.user.getUserAddress(userId)
        if (data?.address) {
          // Remove any fields that are null or ''
          Object.keys(data?.address).forEach((key) => {
            if (data?.address[key] === null || data?.address[key] === '') {
              delete data?.address[key]
            }
          })

          const updatedAddress = {
            ...data?.address,
            billerName: data?.name || '',
          }

          setBillingAddress((prev) => ({
            ...prev,
            ...updatedAddress,
          }))
          setIntialStateAddressObj((prev) => ({
            ...prev,
            ...updatedAddress,
          }))
          setIsLoading(false)
        } else {
          setIsLoading(false)
        }
      } catch (e) {
        setIsLoading(false)
        Error.showError(e)
      }
    }
    getUserAddress()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isLoading) {
    return (
      <div className='payment-form-shimmer mt-2'>
        {[1, 2, 3, 4, 5, 6].map((number, index) => (
          <React.Fragment key={index}>
            <ShimmerText line={1} />
            <ShimmerTitle
              line={1}
              variant='primary'
              className='input-shimmer mb-4'
              key={index}
            />
          </React.Fragment>
        ))}
        <ShimmerButton size='md' />
      </div>
    )
  }

  const checkAddressChangeHandler = (values) => {
    const initialObjectKeys = Object.keys(intialStateAddressObj).sort()
    const addressObjectKeys = Object.keys(values).sort()
    const areEqual = initialObjectKeys.every((key, index) => {
      const objValue1 = intialStateAddressObj[key]
      const objValue2 = values[addressObjectKeys[index]]
      return objValue1 === objValue2
    })
    return areEqual ? false : true
  }

  const onSubmit = async (values) => {
    const isAddressObjChanged = checkAddressChangeHandler(values)
    if (selectedCardDetails?.id) {
      setIsSubmitting(false)
      onSuccess(
        isAddressObjChanged ? values : null,
        isAddressObjChanged,
        selectedCardDetails?.id,
        values?.billerName,
      ) // addressObj = null
    } else {
      try {
        setIsSubmitting(true)
        const data = await stripe.createPaymentMethod({
          type: 'card',
          card: elements.getElement('cardNumber'),
          billing_details: {
            name: user?.fullName,
            email: user?.email,
            address: {},
          },
        })
        if (data && !data?.error) {
          const { data: cardData } = await API.payment.attachPayMethod({
            paymentMethodId: data?.paymentMethod?.id,
          })
          if (cardData) {
            setIsSubmitting(false)
            onSuccess(
              isAddressObjChanged ? values : null,
              isAddressObjChanged,
              data?.paymentMethod?.id,
            ) // addressObj = null
          }
        } else {
          setIsSubmitting(false)
        }
      } catch (e) {
        setIsSubmitting(false)
        Error.showError(e)
      }
    }
  }

  return (
    <Formik
      initialValues={billingAddress}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize={true}>
      {({ handleSubmit, values, handleBlur }) => (
        <>
          <RBForm
            onSubmit={(e) => {
              e.preventDefault()
              handleSubmit(e)
            }}
            as={Form}
            className='my-info px-0 mt-24px'>
            <div className='add-card'>
              <div className='form-group credit-card-wrp'>
                <InputField
                  label='BillerName'
                  name='billerName'
                  type='text'
                  size='lg'
                  sm='10'
                />
              </div>
              <div className='form-group credit-card-wrp'>
                <InputField
                  label='AddressLine1'
                  name='line1'
                  type='text'
                  size='lg'
                  sm='10'
                />
              </div>
              <div className='form-group credit-card-wrp'>
                <InputField
                  label='AddressLine2'
                  name='line2'
                  type='text'
                  size='lg'
                  sm='10'
                />
              </div>
              <Row className='justify-content-between'>
                <Col className='col-6'>
                  <InputField
                    label='Country'
                    name='country'
                    type='text'
                    size='lg'
                    sm='10'
                  />
                </Col>
                <Col className='col-5'>
                  <InputField
                    label='City'
                    name='city'
                    type='text'
                    size='lg'
                    sm='10'
                  />
                </Col>
              </Row>
              <Row className='justify-content-between'>
                <Col className='col-6'>
                  <InputField
                    label='State'
                    name='state'
                    type='text'
                    size='lg'
                    sm='10'
                  />
                </Col>
                <Col className='col-5'>
                  <InputField
                    label='PostalCode'
                    name='postal_code'
                    type='number'
                    size='lg'
                    sm='10'
                  />
                </Col>
              </Row>
              <div className='d-flex justify-content-between'>
                <span className='s6 align-self-center'>Total Amount</span>
                <span className='s6'>
                  {renderPlanPayment(
                    staticSOWData ? staticSOWData : subscription,
                  )}{' '}
                  USD
                </span>
              </div>
              <Row className='d-flex justify-content-between'>
                <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                  <Button
                    className='text-uppercase payment-back-btn line-height-normal w-100 btn-fixed-height s9 mt-24px'
                    onClick={onBack}
                    disabled={isSubmitting || isPaying}>
                    Back
                  </Button>
                </Col>
                <Col xl={8} lg={8} md={8} sm={8} xs={8}>
                  <Button
                    type='submit'
                    variant='saffron'
                    className='border-radius-normal border-none w-100 mt-24px btn-fixed-height'
                    disabled={isSubmitting || isPaying}
                    isLoading={isSubmitting || isPaying}
                    size='md'>
                    MAKE PAYMENT
                  </Button>
                </Col>
              </Row>
            </div>
          </RBForm>
        </>
      )}
    </Formik>
  )
}

const CoachingPlanPaymentFormWithStripe = ({
  selectedCardDetails,
  staticSOWData,
  subscription,
  onSuccess,
  isLoading,
  onBack,
}) => {
  return (
    <Elements
      stripe={stripePromise}
      options={{
        fonts: [
          {
            cssSrc: 'https://fonts.googleapis.com/css?family=Open+Sans',
          },
        ],
      }}>
      <CoachingPlanPaymentForm
        selectedCardDetails={selectedCardDetails}
        staticSOWData={staticSOWData}
        subscription={subscription}
        onSuccess={onSuccess}
        isLoading={isLoading}
        onBack={onBack}
      />
    </Elements>
  )
}

export default CoachingPlanPaymentFormWithStripe
